import React, { useState } from 'react';
import { Mail, Plus, Trash2, Loader2, Search, X, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, SeverityBadge, IconButton, ErrorState, LoadingState, EmptyState } from '@/components/primitives';
import { timeAgo } from '@/hooks/useSystemStatus';

const SOURCE_LABEL = { manual: 'Manual', auto_enumerated: 'Auto-discovered', domain_discovery: 'Discovered' };

/**
 * The backend sets severity only after a check ("unknown" before, "safe"
 * when nothing was found), so an address that was never checked says so
 * instead of borrowing a LOW badge.
 */
function CheckStatus({ email }) {
  if (!email.last_checked) return <Badge plain>Not checked yet</Badge>;
  const sev = String(email.severity || '').toLowerCase();
  if (sev === 'safe') return <Badge tone="success" plain icon={CheckCircle2}>No breaches found</Badge>;
  if (!sev || sev === 'unknown') return <Badge plain>Result unknown</Badge>;
  return <SeverityBadge severity={sev} size="sm" />;
}

function AddEmailForm({ onAdd, onCancel }) {
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const email = value.trim();
    if (!email || pending) return;
    setPending(true);
    try {
      await onAdd(email);
      setValue('');
      onCancel();
    } catch { /* toast shown by the caller */ } finally {
      setPending(false);
    }
  };

  return (
    <form className="is-card ws-add-card" onSubmit={submit} aria-label="Add an email address">
      <div className="ws-add-row">
        <div className="ws-field">
          <label className="ws-label" htmlFor="add-email-input">Email address</label>
          <Input
            id="add-email-input"
            type="email"
            className="ws-input-inset ws-mono"
            placeholder="user@example.com"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
        </div>
        <Button type="submit" disabled={pending || !value.trim()}>
          {pending ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Plus aria-hidden="true" />}
          Add
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

export default function EmailList({ emails, selectedDomain, checking, onRetry, onAdd, onCheck, onRemove, onClearFilter }) {
  const [adding, setAdding] = useState(false);
  const all = emails.data?.emails || [];
  const list = selectedDomain ? all.filter((e) => (e.domain || e.email.split('@')[1]) === selectedDomain) : all;
  const total = emails.data?.total ?? all.length;

  if (emails.loading && !emails.data) return <LoadingState label="monitored emails" rows={5} />;
  if (emails.error && !emails.data) {
    return <ErrorState title="Couldn't load monitored emails" error={emails.error} onRetry={onRetry} />;
  }

  return (
    <div className="ws-stack">
      {emails.error && (
        <ErrorState variant="inline" title="Showing the last loaded emails" error={emails.error} onRetry={onRetry} />
      )}
      <div className="ws-toolbar">
        {selectedDomain && (
          <Badge tone="accent" plain>
            <span className="ws-mono">@{selectedDomain}</span>
            <IconButton label="Show all domains" icon={X} size="sm" tooltip={false} onClick={onClearFilter}
              style={{ width: 18, height: 18, marginRight: -4, color: 'inherit' }} />
          </Badge>
        )}
        <span className="ws-meta">
          {selectedDomain
            ? `${list.length} of ${all.length} addresses`
            : all.length < total ? `Showing ${all.length} of ${total} addresses` : `${all.length} address${all.length === 1 ? '' : 'es'}`}
        </span>
        <span className="ws-toolbar-spacer" />
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" /> Add email
          </Button>
        )}
      </div>

      {adding && <AddEmailForm onAdd={onAdd} onCancel={() => setAdding(false)} />}

      {list.length === 0 ? (
        <EmptyState
          icon={Mail}
          title={selectedDomain ? `No monitored addresses on ${selectedDomain}` : 'No addresses monitored yet'}
          description="Add an address, or add a domain and let Breach Monitor discover its common role addresses."
          action={!adding && <Button onClick={() => setAdding(true)}><Plus aria-hidden="true" /> Add email</Button>}
        />
      ) : (
        <div className="ws-table" role="table" aria-label="Monitored email addresses">
          <div className="ws-email-row ws-email-row--head" role="row">
            <span role="columnheader">Email</span>
            <span role="columnheader">Source</span>
            <span role="columnheader">Breaches</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Last check</span>
            <span role="columnheader"><span className="sr-only">Actions</span></span>
          </div>
          {list.map((e) => {
            const busy = Boolean(checking[e.id]);
            const checked = Boolean(e.last_checked);
            return (
              <div className="ws-email-row" role="row" key={e.id}>
                <span role="cell" className="ws-email-addr ws-truncate" title={e.email}>{e.email}</span>
                <span className="ws-email-meta">
                  <span role="cell"><span className="ws-cell-label">Source</span><Badge size="sm" plain>{SOURCE_LABEL[e.source] || e.source || 'Manual'}</Badge></span>
                  <span role="cell" className={`ws-email-count ${e.breach_count > 0 ? 'is-hot' : ''}`}>
                    <span className="ws-cell-label">Breaches</span>{checked ? e.breach_count || 0 : '-'}
                  </span>
                  <span role="cell"><CheckStatus email={e} /></span>
                  <span role="cell" className="ws-meta">
                    <span className="ws-cell-label">Last check</span>
                    {checked ? <time dateTime={e.last_checked} title={new Date(e.last_checked).toLocaleString()}>{timeAgo(e.last_checked)}</time> : 'Never'}
                  </span>
                </span>
                <span role="cell" className="ws-email-actions">
                  <Button variant="outline" size="sm" onClick={() => onCheck(e)} disabled={busy} aria-label={`Check ${e.email} now`}>
                    {busy ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Search aria-hidden="true" />}
                    {busy ? 'Checking' : 'Check'}
                  </Button>
                  <IconButton label={`Stop monitoring ${e.email}`} icon={Trash2} variant="danger" onClick={() => onRemove(e)} />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
